import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Auth from "../../service/Auth";
import { useAuth } from "../../providers/AuthContext";
import { FaEye, FaEyeSlash, FaLock, FaUserCircle } from "react-icons/fa";
import { CONFIG } from "../../helpers/constants";
import { toast } from "react-toastify";
import clsx from "clsx";
import { motion } from "framer-motion";
import IconClose from "../../assets/icons/icon-close.svg";

const LoginForm = ({ onLoginSuccess, isDrawer }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwords, setPasswords] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { login, updateUser } = useAuth();
  const navigate = useNavigate();
  const authApi = new Auth(CONFIG.BASE_URL);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authApi.loginUser({ username, password });
      if (response.success) {
        updateUser(response.user);

        if (onLoginSuccess) {
          onLoginSuccess(response.user);
        }

        setErrorMessage("");
        if (response.user.role === "User") {
          if (!response.user.isPasswordChanged) {
            navigate("/change-password");
          } else {
            navigate("/home");
          }
        } else {
          navigate("/admin/transfer");
        }
      } else {
        toast.error(response.message || "Failed to login. Please try again.");
      }
    } catch (error) {
      console.error("Error during login:", error);
      toast.error(error.response?.data.message || "An error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={clsx("flex gap-2", isDrawer && "flex-col mt-8")}>
      <div className="relative min-w-[200px]">
        <div className="relative">
          {/* Icon */}
          <span className="absolute inset-y-0 left-0 pt-2 pl-3">
            <FaUserCircle className="text-primary-dark/50" size={18} />
          </span>
          {/* Input Field */}
          <input
            type="text"
            placeholder="Username"
            className="bg-no-repeat bg-left appearance-none rounded-lg p-2 pl-10 text-sm text-black w-full mb-4 border-0"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            required
            autoComplete="off"
            style={{ fontSize: "16px" }}
          />
        </div>
      </div>

      {/* Password Field */}
      <div className="relative min-w-[200px]">
        <div className="relative">
          {/* Input Field */}

          <span className="absolute inset-y-0 left-0 pt-2 pl-3">
            <FaLock className="text-primary-dark/50" size={18} />
          </span>
          {/* Toggle Icon */}
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            className="bg-no-repeat bg-left appearance-none  rounded-lg p-2 pl-10 text-sm text-black w-full mb-4 border-0"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="off"
          />
          <span onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0  pt-2 right-3">
            {!showPassword ? <FaEye className="text-primary-dark/50" size={18} /> : <FaEyeSlash className="text-primary-dark/50" size={18} />}
          </span>
        </div>
        {!isDrawer && (
          <div className="flex flex-col items-center justify-center absolute -bottom-1 right-0">
            <span className="flex flex-row items-center justify-center">
              <button
                type="button"
                className="text-sm font-lighter flex gap-4 items-center text-primary bg-no-repeat cursor-pointer [&_svg_path]:fill-primary"
              >
                Forgot Password ?
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {errorMessage && <div className="text-red-500 text-sm mb-4">{errorMessage}</div>}

      {/* Submit Button */}
      <button
        type="submit"
        className="bg-[#575c75] w-full font-medium px-3 py-2 rounded-lg uppercase leading-3 text-sm transition duration-300 h-9  text-white disabled:opacity-50 hover:bg-[#575c75]/80"
        disabled={isLoading}
      >
        {isLoading ? "Loading..." : "Login"}
      </button>
      {isDrawer && (
        <div className="flex flex-col items-start">
          <span className="flex flex-row items-center justify-center">
            <button
              type="button"
              className="text-sm font-lighter flex gap-4 items-center text-primary bg-no-repeat cursor-pointer [&_svg_path]:fill-primary"
            >
              Forgot Password ?
            </button>
          </span>
        </div>
      )}
    </form>
  );
};

const Login = ({ onLoginSuccess, isOpen = false, setIsOpen }) => {
  useEffect(() => {
    const body = document.querySelector("body");

    if (isOpen) {
      body.classList.add("overflow-y-hidden");
    } else {
      body.classList.remove("overflow-y-hidden");
    }

    // Clean-up in case the component is unmounted
    return () => {
      body.classList.remove("overflow-y-hidden");
    };
  }, [isOpen]);
  return (
    <div className="w-full max-w-lg mx-auto mt-3">
      <div className="hidden md:block">
        <LoginForm onLoginSuccess={onLoginSuccess} />
      </div>
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: isOpen ? "0%" : "100%" }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-0 right-0 w-64 h-full bg-[#383838] shadow-lg z-50 p-4"
      >
        {/* Close Button */}
        <button className="absolute top-2 right-4" onClick={() => setIsOpen(false)}>
          <img className="w-5" src={IconClose} alt="icon button" />
        </button>

        <LoginForm isDrawer onLoginSuccess={onLoginSuccess} />
      </motion.div>
      {isOpen && <div className="fixed top-0 left-0 w-screen h-screen z-10 bg-black/50"></div>}
    </div>
  );
};

export default Login;
